'use client';


import CartDialog from '@/features/cart/components/CartDialog';
import { openCart, useCartStore } from '@/features/cart/store/cart';
import { UserAvatar } from '@/components/common/UserAvatar/UserAvatar';
import { useAuth } from '@/hooks/useAuth';
import useQueryParams from '@/hooks/useQueryParams';
import { Badge } from '@andritzot/metris-web-ui/data-display/badge';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Menu } from '@andritzot/metris-web-ui/navigation/menu';
import { InfoOutlined, ShoppingCartOutlined } from '@mui/icons-material';
import { signIn, useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import UserMenuPopup from './UserMenuPopup';
import { ViewProfile } from './ViewProfile';

interface RightContainerProps {
  isLoggedIn: boolean;
}

const cartButtonSx = {
  '&.MuiButton-outlined': {
    borderColor: '#fff',
    color: '#fff',
  },
  '&.MuiButton-outlined:hover': {
    borderColor: '#fff',
    color: '#fff',
  },
  '& .MuiButton-startIcon': { color: '#fff' },
};

const RightContainer = ({ isLoggedIn }: RightContainerProps) => {
  const { status, update } = useSession();
  const { user } = useAuth();
  const { queryParams, setQueryParams } = useQueryParams();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showUser, setShowUser] = useState(false);
  const [viewProfile, setViewProfile] = useState(false);
  const cartProducts = useCartStore((s) => s.cartProducts);

  useEffect(() => {
    if (queryParams.has('error')) {
      toast.error(queryParams.get('error') ?? 'Unknown error', { autoClose: false, toastId: 'login-error' });
      setQueryParams({ error: null });
    }
  }, [queryParams, setQueryParams]);

  const updateRef = useRef(update);
  useEffect(() => {
    updateRef.current = update;
  }, [update]);

  useEffect(() => {
    if (!isLoggedIn || status !== 'unauthenticated') return;
    const timeoutId = setTimeout(() => {
      void updateRef.current();
    }, 3000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoggedIn, status]);

  const onClickMenu = useCallback(() => {
    setShowUser((prev) => !prev);
    setViewProfile(false);
  }, []);

  const openViewProfile = useCallback(() => {
    setViewProfile(true);
    setShowUser(false);
  }, []);

  const handleCloseUserDialog = useCallback(() => {
    setShowUser(false);
  }, []);

  const handleCloseViewProfile = useCallback(() => {
    setViewProfile(false);
  }, []);

  const handleLogin = useCallback(async () => {
    await signIn('keycloak');
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        ml: 'auto',
        mr: { xs: '1rem', sm: 0 },
        '& a': { textDecoration: 'none' },
      }}
    >
      {!isLoggedIn && (
        <Box>
          {cartProducts.length ? (
            <Badge
              badgeContent={cartProducts.length}
              color="success"
              sx={{
                '& .MuiBadge-badge': {
                  top: 8,
                  right: 4,
                },
              }}
            >
              <Button variant="outlined" startIcon={<ShoppingCartOutlined />} sx={cartButtonSx} onClick={openCart}>
                My Cart
              </Button>
            </Badge>
          ) : (
            <Tooltip title="Could not find what you were looking for?">
              <IconButton variant="default" sx={{ color: '#fff' }} onClick={openCart}>
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <CartDialog />
      {isLoggedIn ? (
        <>
          <Box
            component="button"
            tabIndex={0}
            type="button"
            onKeyDown={onClickMenu}
            onClick={onClickMenu}
            sx={{ background: 'transparent', border: 0, cursor: 'pointer', outline: 0, p: 0, m: 0 }}
          >
            <Box ref={setAnchorEl} sx={{ display: 'inline-block', ml: '15px' }}>
              <UserAvatar name={user?.name} pictureGuid={user?.pictureGuid} size={32} />
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={showUser}
            onClose={handleCloseUserDialog}
            disableScrollLock
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 300, mt: 1 } }, list: { sx: { py: 0 } } }}
          >
            <UserMenuPopup openViewProfile={openViewProfile} handleClose={handleCloseUserDialog} />
          </Menu>
          <ViewProfile visibleProfile={viewProfile} handleCloseProfile={handleCloseViewProfile} />
        </>
      ) : (
        <Button variant="text" onClick={handleLogin}>
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: '#fff',
              display: 'block',
              fontSize: '14px',
              textTransform: 'uppercase',
              px: '1px',
            }}
          >
            LOGIN
          </Box>
        </Button>
      )}
    </Box>
  );
};

export default RightContainer;
